/**
 * Needs-you kind taxonomy — the CLOSED set of nine kinds (founder-locked
 * 2026-07-14; see bash-notes/home-briefing-build-plan-2026-07-14.md law 4).
 * Color carries the NATURE of the ask. Every producer of a needs-you card
 * must map into this set; do not add kinds without a founder ruling.
 */
export const NEED_KINDS = [
  'signoff', 'proposal', 'question', 'fix', 'connect',
  'permission', 'spend', 'feedback', 'handoff',
] as const;

export type NeedKind = (typeof NEED_KINDS)[number];

export const NEED_KIND_LABELS: Record<NeedKind, string> = {
  signoff: 'Sign-off',
  proposal: 'Proposal',
  question: 'Question',
  fix: 'Fix',
  connect: 'Connect',
  permission: 'Permission',
  spend: 'Spend',
  feedback: 'Feedback',
  handoff: 'Handoff',
};

/** CSS custom-property name (defined in apps/web globals.css, light + dark). */
export const NEED_KIND_CSS_VAR: Record<NeedKind, string> = {
  signoff: '--kind-amber',
  // Emerald, not indigo (founder 07-22): indigo sat one hue off permission
  // violet, so the two card species blurred. Proposals are Nami-born and
  // carry Nami's green; permissions keep violet.
  proposal: '--kind-emerald',
  question: '--kind-blue',
  fix: '--kind-red',
  connect: '--kind-orange',
  permission: '--kind-violet',
  spend: '--kind-green',
  feedback: '--kind-rose',
  handoff: '--kind-coral',
};

/**
 * Map an SDK InteractionKind literal (interactions.kind column) into the
 * taxonomy. Unknown/future kinds read as 'question' — an honest "the task
 * needs a human" default that never crashes the desk.
 * PR-3 refines: composio_auth splits connect-vs-fix by expiry state.
 * Done rail (08-22): an ask_question row may carry `intent: 'handoff'` in its
 * request (request_user_input / done-verdict mint sites) — the user must DO
 * something or hand something over, so the card wears the handoff kind. The
 * second arg is optional and every legacy call site passes one arg unchanged.
 */
export function needKindForInteraction(kind: string, request?: { intent?: string; renderer?: string } | null): NeedKind {
  switch (kind) {
    case 'ask_question': return request?.intent === 'handoff' ? 'handoff' : 'question';
    case 'composio_auth': return 'connect';
    case 'tool_permission': return 'permission';
    // Agent email: the server's own yes before an email goes (a first contact, or files to someone else).
    case 'email_send_approval': return 'permission';
    case 'credit_approval': return 'spend';
    // Engines T5: a used-up plan is a decision the person owns (credits, another plan, or
    // wait), classified like its nearest neighbour, the credit approval.
    case 'plan_limit': return 'spend';
    case 'exit_plan': return 'signoff';
    case 'hands_approval': return 'handoff';
    // Agent wallet: the purchase card (renderer 'purchase', wallet/frames.ts PURCHASE_RENDERER, kept a literal so
    // this leaf imports no schema) is a spend decision, classed with the credit approval.
    case 'custom':
      return request?.renderer === 'purchase' ? 'spend' : 'question';
    default:
      return 'question';
  }
}
